import { createStore } from 'vuex';
import { THEME_LIGHT, chartTheme } from '@rdrive/theme';

// THE APP STORE — DOMAIN state only (IA_ABSORPTION_PLAN Phase 3 step 8, the shape
// project-overview's took in Phase 1 step 6b-2b): the status templates and the logo read from
// DuckDB, the FAB's status filters and "overdue only", the welcome/load flags, theme and build
// mode. The PLATFORM state and services — URL settings, the proxy transport, DuckDB, the data
// loader, the two filter contexts, analytics, the boot — are the dashboard's (`createDashboard` /
// `useDashboard()` from @rdrive/plugin-dashboard-shell); pages read them through
// composables/useAppContext.js, App/setup.js through useDashboard().
//
// A FACTORY taking that dashboard: `loadLookupObjectsFromDB` needs its DuckDB connection, and the
// two filter-reset mutations reach its filter contexts. dashboard.config.js passes
// `createAppStore` as the config's `store`, and createDashboard calls it once with the dashboard
// it created.
//
// ONLY WHAT SOMETHING READS (Phase 4 leftovers cleanup, 2026-10-13). Every member below has a
// reader in this app; the readers are named beside each one. Deleted that day, because nothing in
// src/** (nor createDashboard, nor any package — packages never read a store) read them: the
// compat delegates (state `apiClient`, `duckDBService`, `dataLoader`, `isDuckDBInitialized`,
// `settings`; the platform getters `getDuckDBService`…`currentLocale` and `executeFilteredQuery`;
// the settings/token/locale/context mutations and actions — the dashboard owns those writes:
// `setValues`, `setScopedToken`, its filters) and the domain members no page read since the pages
// moved to useAppContext() (`items`/`selectedItems`/`selectedTeam`/`showOpenItemsOnly`/
// `timeZone`, the `static` lookup objects but the status templates, their setters and getters,
// `getStatusFilters`/`getOverdueOnly`/`getBuildMode`/`getFilteredItems`). What remains of the
// delegates is `state.filterContext` / `state.filterStatusHistoryContext`, read by the two
// filter-reset mutations the pages commit. New code reads the dashboard; never add a platform
// value here.
//
// Earlier removals (Phase 3 step 8): the Vuex `analytics` module (→ `dashboard.analytics`, through
// utils/useAnalytics.js), `setDataLoaded` (the data loader owns both halves it set — its own
// `dataLoaded` ref and DuckDBService's query gate), the `'defaultLocale'` settings default and
// `mapLocale` (the dashboard normalises the URL locale once, with @rdrive/i18n's normalizeLocale),
// the axios ApiClient instance (→ the dashboard's stateless ApiClient), and four actions nothing
// dispatched.

/**
 * @param {import('@rdrive/plugin-dashboard-shell/src/dashboardContext.js').Dashboard} dashboard
 * @returns {import('vuex').Store}
 */
export function createAppStore(dashboard) {
  return createStore({
    state: {
      // --- the dashboard's two filter contexts, read through (no copies, no setters — assigning
      // one throws): `filters.issues` / `filters.statusHistory` (createFilterContext contexts from
      // data/filters.js). Read by the resetFilters / resetStatusHistoryFilters mutations below.
      get filterContext() { return dashboard.filters.issues; },
      get filterStatusHistoryContext() { return dashboard.filters.statusHistory; },

      // --- domain state
      // The FAB's selection — App/setup.js (writable computeds handed to useIssueStatusFilters)
      statusFilters: {},
      overdueOnly: false,
      // The welcome dialog and the Load trigger — App/setup.js (getShowWelcomeDialog /
      // getLoadDashboard)
      showWelcomeDialog: true,
      loadDashboard: false,
      // 'light' | 'dark' — App/setup.js (setTheme); read by the chartFontColor getter
      theme: THEME_LIGHT,
      // 'production' until App's onMounted dispatches updateBuildMode('webapp') — useAppContext's
      // `buildMode` (the PNG download, the grid's link opener)
      buildMode: 'production',
      // The company logo (loadLookupObjectsFromDB) — useAppContext's `logoUrl` (the report)
      logoUrl: '',
      // Status id → status_templates row (loadLookupObjectsFromDB) — getStatusTemplatesObj
      statusTemplatesObj: {},
    },
    mutations: {
      // --- the pages' filter resets (each page's onMounted commits them, as project-overview's do)
      resetFilters(state) {
        state.filterContext.resetFilters();
      },
      resetStatusHistoryFilters(state) {
        state.filterStatusHistoryContext.resetFilters();
      },

      // --- domain
      setTheme(state, newTheme) {
        state.theme = newTheme;
      },
      setStatusTemplatesObj(state, statusTemplatesObj) {
        state.statusTemplatesObj = Object.freeze(statusTemplatesObj);
      },
      setLoadDashboard(state, loadDashboard) {
        state.loadDashboard = loadDashboard;
      },
      setShowWelcomeDialog(state, showWelcomeDialog) {
        state.showWelcomeDialog = showWelcomeDialog;
      },
      setBuildMode(state, buildMode) {
        state.buildMode = buildMode;
      },
      setLogoUrl(state, logoUrl) {
        state.logoUrl = logoUrl;
      },
      setStatusFilters(state, filters) {
        state.statusFilters = { ...filters };
      },
      setOverdueOnly(state, value) {
        state.overdueOnly = value;
      },
    },
    actions: {
      // The lookups the app reads from the store, built from the loaded tables — the root
      // component's old post-load queries, verbatim. Run by the data loader's onLoaded hook
      // (dashboard.config.js), after both filter contexts and BEFORE `dataLoaded` flips.
      async loadLookupObjectsFromDB({ dispatch, commit }) {
        const conn = dashboard.conn;

        // Query DuckDB for logoUrl from 'company' table after data is loaded
        try {
          const sql = 'SELECT logoUrl FROM company LIMIT 1;';
          const result = await conn.query(sql);
          const companyData = result.toArray();

          if (companyData && companyData.length > 0 && companyData[0].logoUrl) {
            const logoUrl = companyData[0].logoUrl;
            await dispatch('updateLogoUrl', logoUrl);
          } else {
            console.warn('No data found in company table for logoUrl');
          }
        } catch (queryError) {
          console.error('Error querying company table for logoUrl:', queryError);
        }

        // Fetch and populate status templates in store (only for issues, not forms)
        try {
          const statusResult = await conn.query(`
                    SELECT DISTINCT st.*
                    FROM status_templates st
                    INNER JOIN issues i ON st.Id = i.IssueStatusId
                    ORDER BY st.Sort
                `);
          const statusArray = statusResult.toArray();
          const statusTemplatesObj = {};
          statusArray.forEach(status => {
            statusTemplatesObj[status.Id] = status;
          });
          commit('setStatusTemplatesObj', statusTemplatesObj);
        } catch (statusError) {
          console.error('Error fetching status templates:', statusError);
        }
      },
      updateLoadDashboard({ commit }, loadDashboard) {
        commit('setLoadDashboard', loadDashboard);
      },
      updateShowWelcomeDialog({ commit }, showWelcomeDialog) {
        commit('setShowWelcomeDialog', showWelcomeDialog);
      },
      updateBuildMode({ commit }, buildMode) {
        commit('setBuildMode', buildMode);
      },
      updateLogoUrl({ commit }, logoUrl) {
        commit('setLogoUrl', logoUrl);
      },
    },
    getters: {
      getLoadDashboard: (state) => state.loadDashboard,
      getShowWelcomeDialog: (state) => state.showWelcomeDialog,
      getLogoUrl: (state) => state.logoUrl,
      getStatusTemplatesObj: (state) => state.statusTemplatesObj,
      // `state.theme` is 'light' | 'dark' (App/setup.js commits nothing else): the same two colours
      // the getter hard-coded (#000000 / #FFFFFF), now from @rdrive/theme's tokens.
      chartFontColor: (state) => chartTheme(state.theme).fontColor,
    },
  });
}
